export interface PlusOne {
  id: string;
  name: string;
  email?: string;
  relationship?: string;
  dietaryRestrictions?: string[];
  age?: number;
  /** ID of an existing Guest record this plus-one is linked to */
  linked_guest_id?: string;
}

/**
 * kalligo-api's PlusOneSchema requires `relationship` (contracts/
 * planningGuests.ts) but the New Guest form's plus-one UI never collects it
 * — every plus-one saved without one 400'd, taking the whole guest with it.
 * Defaulting client-side rather than changing the API contract (a separate
 * repo, and a real "should this be required at all" design question) or
 * adding a picker (a UI feature beyond this bug fix's scope). Single home
 * for the value BulkOperations.tsx already hardcoded independently.
 */
export const DEFAULT_PLUS_ONE_RELATIONSHIP = 'Guest';

export interface GuestTag {
  id: string;
  label: string;
  color: string;
  category: 'relationship' | 'group' | 'dietary' | 'special';
}

// Never 'archived': that is the separate API archived flag (Guest.archived).
export type GuestStatus = 'active' | 'inactive';
// 'awaiting_plus_one' is a real status the backend/DB uses (a guest confirmed
// but their plus-one count is unresolved) — it was missing from this union, so
// 121 such guests fell through the RSVP tiles and the counts didn't sum.
export type RSVPStatus = 'confirmed' | 'declined' | 'pending' | 'awaiting_plus_one';

export interface Guest {
  id: string;
  reference_id?: number | null;
  // name kept for backward compat with SeatingChart / PlannerWidgets / legacy code
  name: string;
  email: string;
  phone?: string;
  avatar: string;
  tags: GuestTag[];
  status: GuestStatus;
  // snake_case API fields
  rsvp_status: RSVPStatus;
  /** RSVP status derived by the API from the per-event answers; rsvp_status is the stored legacy value. */
  rsvp_status_derived?: RSVPStatus | null;
  rsvp_date?: string | null;   // ISO string
  /** Stamped by every guest portal login. Distinct from rsvp_date, which
   *  only moves on an actual RSVP submission — this is the only signal
   *  that a guest opened and authenticated into the portal at all. */
  last_login_at?: string | null;
  plus_ones: PlusOne[];
  created_at: Date;
  updated_at: Date;
  // Optional extended UI fields
  group?: string;
  // Free text from the API: 'bride', 'groom', or another family side (e.g. 'osim', 'alfa-grace').
  side?: string | null;
  notes?: string;
  dietary_restrictions?: string[];
  table_id?: string | number;
  checked_in?: boolean;
  archived?: boolean;
  household_id?: string | null;
  /** Part this guest plays at the event (bride, best_man, usher…); independent of is_honoree. */
  role?: string | null;
  /** True for the guest(s) the event is celebrating; unlocks the honoree profile. */
  is_honoree?: boolean;
  /** Formatted guest ref (e.g. GS-001) of the honoree this guest's party membership belongs to. */
  party_of?: string | null;
  party_role?: string | null;
  /** Per-guest code for the self-serve RSVP portal — see ans-backend's
   *  guests.invite_code / routes/event-day/portal.ts. */
  invite_code?: string | null;
  /** True once this guest has set a self-serve portal password — email is
   *  only mandatory from that point on, since that's what they log in with. */
  portal_onboarded?: boolean;
  // Legacy camelCase aliases kept for compatibility — prefer snake_case above
  /** @deprecated use rsvp_status */
  rsvpStatus?: RSVPStatus;
  /** @deprecated use rsvp_date */
  rsvpDate?: Date;
  /** @deprecated use plus_ones */
  plusOnes?: PlusOne[];
  /** @deprecated use created_at */
  createdAt?: Date;
  /** @deprecated use updated_at */
  updatedAt?: Date;
}

export function getFullName(g: Pick<Guest, 'name' | 'email'>): string {
  return g.name || g.email || 'Unknown';
}

export interface GuestGroup {
  id: string;
  name: string;
  description?: string;
  color: string;
  guestIds: string[];
}

export interface RSVPForm {
  guestId: string;
  response: RSVPStatus;
  plusOnes: number;
  dietaryRestrictions?: string[];
  specialRequests?: string;
  accommodation?: boolean;
  transportation?: boolean;
  submittedAt: Date;
}

export interface GuestCommunication {
  id: string;
  type: 'email' | 'sms';
  subject?: string;
  content: string;
  recipients: string[]; // Guest IDs
  status: 'draft' | 'scheduled' | 'sent' | 'failed';
  scheduledFor?: Date;
  sentAt?: Date;
  createdAt: Date;
}

export interface GuestImportData {
  name: string;
  email: string;
  phone?: string;
  tags?: string[];
  group?: string;
  plusOnes?: number;
}

export interface GuestFilter {
  status?: GuestStatus[];
  rsvpStatus?: RSVPStatus[];
  tags?: string[];
  groups?: string[];
  searchTerm?: string;
}

export interface GuestStats {
  totalGuests: number;
  confirmedGuests: number;
  pendingRSVPs: number;
  declinedGuests: number;
  /** RSVP statuses beyond confirmed/pending/declined — without these the tiles
   *  didn't sum to the total (126 guests were invisible). */
  awaitingPlusOne: number;
  /** Catch-all so confirmed+pending+declined+awaiting+other === total. */
  otherStatus: number;
  totalPlusOnes: number;
  responseRate: number;
  capacityLimit?: number;
  budgetLimit?: number;
  estimatedCost?: number;
  overCapacity?: boolean;
  overBudget?: boolean;
}

export interface ProjectCapacity {
  maxGuests: number;
  maxConfirmed: number;
  venueCapacity?: number;
  budgetPerGuest?: number;
  currentConfirmed: number;
  currentPending: number;
  bufferPercentage: number;
}

export interface GuestBudget {
  totalBudget: number;
  costPerGuest: number;
  costPerChild?: number;
  currentEstimatedCost: number;
  remainingBudget: number;
  overBudget: boolean;
}

export interface PlusOnePolicy {
  allowPlusOnes: boolean;
  maxPlusOnes: number;
  allocationStrategy: 'fair' | 'selective' | 'none';
  eligibleTags: string[];
  plusOneLimit: number;
  currentPlusOnes: number;
}

/**
 * Invite-sponsor quota (kalligo-api PR #700's invite_sponsors resource) —
 * e.g. "the bride can invite 100 guests, her mother Grace can invite 200",
 * enforced server-side and race-safe. `used` is
 * COUNT(DISTINCT guest_id) of guests currently invited under this sponsor
 * across ALL events (not per-event), computed server-side — never derive it
 * client-side. `linked_guest_id` is optional: a sponsor need not be a guest.
 */
export interface InviteSponsor {
  id: string;
  project_id: string;
  name: string;
  quota: number;
  linked_guest_id: string | null;
  // set by the API for the Bride/Groom sponsor
  side: 'bride' | 'groom' | null;
  used: number;
  created_at: string;
  updated_at: string;
}

/** GET /invite-sponsors/:id/usage response — a lighter-weight read than the full sponsor record. */
export interface InviteSponsorUsage {
  sponsor_id: string;
  name: string;
  quota: number;
  used: number;
  remaining: number;
}

export interface RSVPDeadline {
  enabled: boolean;
  deadline: Date;
  reminderDays: number[];
  autoDeclineAfterDeadline: boolean;
  reminderMessages: {
    firstReminder?: string;
    secondReminder?: string;
    finalReminder?: string;
  };
}

export interface BulkAction {
  type: 'update-status' | 'update-rsvp' | 'add-tags' | 'remove-tags' | 'send-communication' | 'export';
  guestIds: string[];
  data?: any;
}

export interface GuestTemplate {
  id: string;
  name: string;
  description: string;
  defaultTags: GuestTag[];
  communicationTemplates: GuestCommunication[];
}
